import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import HeartIcon from "@icons/HeartIcon";
import MoreHorizontalIcon from "@icons/MoreHorizontalIcon";
import StarIcon from "@icons/StarIcon";
import AppsIcon from "@icons/AppsIcon";
import BallotIcon from "@icons/BallotIcon";
import MenuBookIcon from "@icons/MenuBookIcon";
import LatestUpdates from "./LatestUpdates";
import { auditLogService } from "@services/auditLog";
import { getCurrentUserId } from "@utils/userUtils";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import { getNewsFromUrl, getUrlLinks, sendConversation, getUserInterests, saveUserInterests, ConversationResponse, Article, ArticleSource, DiscoverLink } from "@services/discover";
import { GlobalAdminLlmPromptButton } from "@components/Spaces/GlobalAdminLlmPromptButton";
import { toast } from "react-toastify";
import CmsSourceLink from "./CmsSourceLink";

// Import the JSON data
import discoveryData from "../../data/discoveryData.json";

// Types

// Article type imported from @services/discover

// Styled Components
const DiscoveryContainer = styled(Box)({
  backgroundColor: "#fdffff",
  borderRadius: "12px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
});

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  padding: "0px 64px 24px",
  display: "flex",
  alignItems: "end",
  justifyContent: "space-between",
  height: "80px",
  position: "sticky",
  top: 0,
  zIndex: 10,
  [theme.breakpoints.down("md")]: {
    padding: "0px 16px 16px",
    height: "auto",
    alignItems: "center",
    flexDirection: "column",
    gap: "8px",
  },
}));

const HeaderLeft = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const HeaderTitle = styled(Typography)({
  fontSize: "28px",
  fontWeight: 500,
  color: "#262626",
  letterSpacing: "-1px",
  lineHeight: 1.5,
});

const SourceTabsWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasActive",
})<{ hasActive?: boolean }>(({ hasActive }) => ({
  display: "inline-flex",
  alignItems: "stretch",
  borderRadius: "12px",
  backgroundColor: "#eafdff",
  border: "1px solid #99d4e1",
  padding: "5px",
  gap: hasActive ? "4px" : 0,
  fontFamily: '"Rethink Sans", sans-serif',
}));

const SourceTab = styled(Box)<{ $active?: boolean }>(({ $active }) => ({
  fontFamily: '"Rethink Sans", sans-serif',
  padding: "10px 24px",
  borderRadius: "8px",
  fontSize: "15px",
  fontWeight: 600,
  letterSpacing: "-0.2px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  backgroundColor: $active ? "#0a829d" : "transparent",
  color: $active ? "#ffffff" : "#05495a",
  "&:hover": {
    backgroundColor: $active ? "#098494" : "#d8f0f4",
    color: $active ? "#ffffff" : "#05495a",
  },
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "16px 48px",
  overflowY: "auto",
  display: "flex",
  gap: "16px",
  [theme.breakpoints.down("md")]: {
    padding: "12px 16px",
    flexDirection: "column",
  },
}));

const MainContent = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const Sidebar = styled(Box)(({ theme }) => ({
  width: "340px",
  flexShrink: 0,
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const NewsCard = styled(Box)<{ featured?: boolean }>(({ featured }) => ({
  backgroundColor: "#fbfeff",
  border: "1px solid #b9e3ed",
  borderRadius: "8px",
  padding: "20px",
  display: "flex",
  gap: featured ? "16px" : "0",
  // Use column layout; featured cards manage their own top row so
  // ASK AI section can span full width below image + content.
  flexDirection: "column",
  height: "fit-content",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#f0f8fb",
    border: "1px solid #99d4e1",
    transform: "translateY(-1px)",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  },
}));

const NewsCardContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
});

const NewsCardHeader = styled(Typography)({
  fontSize: "24px",
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: "-0.4px",
  color: "#000",
  marginBottom: "8px",
});

const NewsCardHeaderSmall = styled(Typography)({
  fontSize: "15px",
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: "-0.4px",
  color: "#000",
  marginBottom: "4px",
});

const NewsCardTime = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  marginBottom: "8px",
});

const NewsCardBody = styled(Typography)({
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: "-0.4px",
  color: "#3b3b3b",
  marginBottom: "16px",
});

const NewsCardFooter = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const SourcesSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
});

const SourceLogos = styled(Box)({
  display: "flex",
  alignItems: "center",
  paddingRight: "6px",
});

const SourceLogo = styled(Box)({
  width: "16px",
  height: "16px",
  borderRadius: "50%",
  backgroundColor: "#edecec",
  marginRight: "-6px",
  border: "1px solid #fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "8px",
  fontWeight: 600,
  color: "#666",
});

const ActionsSection = styled(Box)({
  // Intentionally hidden per design: like icon and "more" menu
  display: "none",
});

const ActionButton = styled(Button)({
  minWidth: "24px",
  width: "24px",
  height: "24px",
  padding: "2px",
  borderRadius: "4px",
  color: "#666",
  "&:hover": {
    backgroundColor: "#f0f0f0",
  },
});

const InterestsCard = styled(Box)({
  backgroundColor: "#eef9fb",
  border: "1px solid #b9e3ed",
  borderRadius: "8px",
  padding: "16px",
});

const InterestsTitle = styled(Typography)({
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: "-0.4px",
  color: "#000",
  marginBottom: "16px",
});

const InterestsDescription = styled(Typography)({
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: "-0.4px",
  color: "#3b3b3b",
  marginBottom: "16px",
});

const InterestsTags = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "6px",
  marginBottom: "12px",
});

const InterestTag = styled(Chip)<{ selected?: boolean }>(({ selected }) => ({
  backgroundColor: selected ? "#99d4e1" : "#eafdff",
  border: "1px solid #99d4e1",
  borderRadius: "6px",
  fontSize: "12px",
  fontWeight: 400,
  padding: "2px 4px",
  height: "26px",
  color: "#05495a",
  "&:hover": {
    backgroundColor: selected ? "#8cc8d6" : "#d8f0f4",
  },
}));

const SaveInterestsButton = styled(Button)({
  width: "100%",
  background: "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)",
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "14px",
  fontWeight: 600,
  color: "#e6fafc",
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(90deg, #096d84 0%, #4ab0c3 100%)",
  },
});

const CategoryPill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: "999px",
  backgroundColor: "#eafdff",
  border: "1px solid #99d4e1",
  fontSize: "11px",
  fontWeight: 600,
  color: "#05495a",
  textTransform: "uppercase",
});

// Card component for the 3-column regular articles grid.
// Tracks image load failure internally so description renders in the correct place.
function RegularArticleCard(props: {
  article: Article;
  onClick: () => void;
  onSourcesClick: (e: React.MouseEvent, sources: (string | ArticleSource)[]) => void;
  onLikeClick: (id: string, e: React.MouseEvent) => void;
  renderSourceLogos: (sources: (string | ArticleSource)[]) => React.ReactNode;
  categoryLabel?: string | null;
  onAskQuestion: (question: string) => void;
}) {
  const { article, onClick, onSourcesClick, onLikeClick, renderSourceLogos, onAskQuestion } = props;
  const [imgFailed, setImgFailed] = React.useState(false);
  const showImage = !!article.image && !imgFailed;
  const showDescription = !showImage && !!article.bodyText;

  return (
    <NewsCard sx={{ flex: 1 }} onClick={onClick}>
      {showImage && (
        <Box sx={{ borderRadius: '9px', overflow: 'hidden', width: '100%', height: '180px', flexShrink: 0 }}>
          <img
            src={article.image}
            alt={article.headline}
            onError={() => setImgFailed(true)}
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        </Box>
      )}

      <Box sx={{ padding: "16px 0" }}>
        {/* Category pill (uses primary/selected category when provided) */}
        {(article as any).categoryLabel && (
          <Box sx={{ mb: "4px" }}>
            <CategoryPill>{(article as any).categoryLabel}</CategoryPill>
          </Box>
        )}

        <NewsCardHeaderSmall>{article.headline}</NewsCardHeaderSmall>

        <NewsCardTime>
          <AppsIcon size={16} color="#666" />
          <Typography sx={{ fontSize: "12px", color: "#3b3b3b" }}>
            {article.publishedTime}
          </Typography>
        </NewsCardTime>

        {showDescription && (
          <NewsCardBody sx={{
            fontSize: "13px",
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            {article.bodyText}
          </NewsCardBody>
        )}

        <NewsCardFooter>
          <SourcesSection
            title="View sources"
            onClick={(e) => onSourcesClick(e, article.sources)}
          >
            {renderSourceLogos(article.sources)}
            <Typography sx={{ fontSize: "14px", color: "#3b3b3b" }}>
              {article.sources.length} sources
            </Typography>
          </SourcesSection>

          <ActionsSection>
            <ActionButton onClick={(e) => onLikeClick(article.id, e)}>
              <HeartIcon filled={article.isLiked} size={16} />
            </ActionButton>
            <ActionButton>
              <MoreHorizontalIcon size={16} />
            </ActionButton>
          </ActionsSection>
        </NewsCardFooter>

        {article.suggested_questions && article.suggested_questions.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "4px",
              mt: "8px",
              minWidth: 0,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#0a829d",
              }}
            >
              ASK AI
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                maxWidth: "100%",
              }}
            >
              {article.suggested_questions.slice(0, 5).map((q, idx) => (
                <Box
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAskQuestion(q);
                  }}
                  sx={{
                    fontSize: "13px",
                    color: "#0a829d",
                    backgroundColor: "#eef9fb",
                    border: "1px solid #b9e3ed",
                    borderRadius: "12px",
                    padding: "3px 10px",
                    cursor: "pointer",
                    lineHeight: 1.5,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    "&:hover": { backgroundColor: "#d4f3fa" },
                  }}
                >
                  {q}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </NewsCard>
  );
}

// Horizontal card (featured + more articles) with image on right — handles broken images
function HorizontalArticleCard(props: {
  article: Article;
  onClick: () => void;
  onSourcesClick: (e: React.MouseEvent, sources: (string | ArticleSource)[]) => void;
  onLikeClick: (id: string, e: React.MouseEvent) => void;
  renderSourceLogos: (sources: (string | ArticleSource)[]) => React.ReactNode;
  categoryLabel?: string | null;
  onAskQuestion: (question: string) => void;
}) {
  const { article, onClick, onSourcesClick, onLikeClick, renderSourceLogos, categoryLabel, onAskQuestion } = props;
  const [imgFailed, setImgFailed] = React.useState(false);
  const showImage = !!article.image && !imgFailed;

  return (
    <NewsCard featured onClick={onClick}>
      {/* Top row: text on left, image on right */}
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          alignItems: "stretch",
        }}
      >
        <NewsCardContent>
          {categoryLabel && (
            <Box sx={{ mb: "4px" }}>
              <CategoryPill>{categoryLabel}</CategoryPill>
            </Box>
          )}

          <NewsCardHeader>{article.headline}</NewsCardHeader>

          <NewsCardTime>
            <AppsIcon size={16} color="#666" />
            <Typography sx={{ fontSize: "12px", color: "#3b3b3b" }}>
              {article.publishedTime}
            </Typography>
          </NewsCardTime>

          <NewsCardBody sx={{ mt: "12px" }}>{article.bodyText}</NewsCardBody>

          <NewsCardFooter>
            <SourcesSection
              title="View sources"
              onClick={(e) => onSourcesClick(e, article.sources)}
            >
              {renderSourceLogos(article.sources)}
              <Typography sx={{ fontSize: "14px", color: "#3b3b3b" }}>
                {article.sources.length} sources
              </Typography>
            </SourcesSection>
            <ActionsSection>
              <ActionButton onClick={(e) => onLikeClick(article.id, e)}>
                <HeartIcon filled={article.isLiked} size={16} />
              </ActionButton>
              <ActionButton onClick={(e) => e.stopPropagation()}>
                <MoreHorizontalIcon size={16} />
              </ActionButton>
            </ActionsSection>
          </NewsCardFooter>
        </NewsCardContent>

        {showImage && (
          <Box
            sx={{
              width: "300px",
              height: "200px",
              flexShrink: 0,
              borderRadius: "9px",
              overflow: "hidden",
            }}
          >
            <img
              src={article.image}
              alt={article.headline}
              onError={() => setImgFailed(true)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        )}
      </Box>

      {/* ASK AI suggested questions: full width under content + image */}
      {article.suggested_questions && article.suggested_questions.length > 0 && (
        <Box
          sx={{
            mt: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "4px",
            minWidth: 0,
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#0a829d",
            }}
          >
            ASK AI
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              maxWidth: "100%",
            }}
          >
            {article.suggested_questions.slice(0, 5).map((q, idx) => (
              <Box
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  onAskQuestion(q);
                }}
                sx={{
                  fontSize: "13px",
                  color: "#0a829d",
                  backgroundColor: "#eef9fb",
                  border: "1px solid #b9e3ed",
                  borderRadius: "12px",
                  padding: "3px 10px",
                  cursor: "pointer",
                  lineHeight: 1.5,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  "&:hover": { backgroundColor: "#d4f3fa" },
                }}
              >
                {q}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </NewsCard>
  );
}

export default function Discovery() {
  const location = useLocation();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [interests, setInterests] = useState<Array<{ id: string; label: string; selected: boolean }>>([]);
  const [discoverLinks, setDiscoverLinks] = useState<DiscoverLink[]>([]);
  const [interestsLoading, setInterestsLoading] = useState(false);
  const [savingInterests, setSavingInterests] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedLinkName, setSelectedLinkName] = useState<string | null>(null);
  const [isSourcesOpen, setIsSourcesOpen] = useState<boolean>(false);
  const [modalSourceIds, setModalSourceIds] = useState<(string | ArticleSource)[]>([]);
  const [loadingNews, setLoadingNews] = useState<boolean>(false);
  const [newsError, setNewsError] = useState<string | null>(null);
  const [discoverLlmPromptsBySection, setDiscoverLlmPromptsBySection] = useState<
    Record<string, string>
  >({});
  const [discoverLlmWallsBySection, setDiscoverLlmWallsBySection] = useState<
    Record<string, number>
  >({});
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [activeQa, setActiveQa] = useState<{
    article: Article | null;
    question: string;
    answer: string;
    followUps: string[];
    sources: any[];
    loading: boolean;
    error: string | null;
  } | null>(null);
  const getCategoryLabelById = (id: string): string => {
    const found = discoveryData.categories.find((c) => c.id === id);
    const label = found?.label ?? id;
    if (id === "latest-updates") return "Regulatory Updates";
    if (id === "all-sources" || id === "all-updates") return "All Sources";
    if (id === "my-sources") return "My Sources";
    return label;
  };

  // Log page access to audit log
  useEffect(() => {
    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      const facility = getSelectedFacilityFromSession();
      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: '/dashboard/discover',
        method: 'GET',
        request_payload: {},
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: 'Discover',
          action: 'VIEW',
        },
        action_type: 'VIEW',
        action_status: 'SUCCESS',
      });
    };
    logPageAccess();
  }, []);

  // Fetch discover links from API and populate interests dynamically — called once on mount only
  useEffect(() => {
    const state = location.state as { cachedInterests?: Array<{ id: string; label: string; selected: boolean }> } | null;
    const hasCachedInterests = state?.cachedInterests && state.cachedInterests.length > 0;
    if (hasCachedInterests) setInterests(state!.cachedInterests!);

    const fetchLinks = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      if (!userId || userId === 0) return;
      setInterestsLoading(true);
      try {
        const [linksResponse, interestsResponse] = await Promise.allSettled([
          getUrlLinks({ user_id: userId, industry_type: 'healthcare' }),
          getUserInterests(userId),
        ]);

        const activeLinks: DiscoverLink[] =
          linksResponse.status === 'fulfilled'
            ? (linksResponse.value.links || []).filter((l) => l.is_active)
            : [];

        setDiscoverLinks(activeLinks);

        if (!hasCachedInterests) {
          const savedIds: Set<string> =
            interestsResponse.status === 'fulfilled'
              ? new Set(interestsResponse.value.category_ids || [])
              : new Set();

          setInterests(
            activeLinks.map((link) => {
              const id = link.pretty_name.toLowerCase().replace(/\s+/g, '-');
              return { id, label: link.pretty_name, selected: savedIds.has(id) };
            })
          );
        } else {
          setInterests(state!.cachedInterests!);
        }
      } catch {
        // silently fall back to empty list
      } finally {
        setInterestsLoading(false);
      }
    };
    fetchLinks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle category filtering from navigation state and fetch news from URL
  useEffect(() => {
    const state = location.state as { category?: string; url?: string; linkName?: string; categories?: Array<{ name: string; urls: string[] }>; cachedArticles?: Article[] } | null;

    // Clear any active AI panel whenever the discover context (category/url) changes
    setActiveQa(null);
    setDiscoverLlmPromptsBySection({});
    setDiscoverLlmWallsBySection({});

    // If returning from an article page with cached articles, restore them without re-fetching
    if (state?.cachedArticles && state.cachedArticles.length > 0) {
      setSelectedCategory(state.category || null);
      setSelectedLinkName(state.linkName || null);
      setArticles(state.cachedArticles);
      return;
    }

    // If multi-category "All Sources" or "My Sources" state, fetch from categories
    if (state?.categories && state.categories.length > 0 && !state.url) {
      setLoadingNews(true);
      setNewsError(null);
      setSelectedCategory(state.category || null);
      setSelectedLinkName(state.linkName || null);

      const fetchAllUpdates = async () => {
        try {
          const userId = parseInt(getCurrentUserId(), 10);
          if (!userId || userId === 0) throw new Error("User not authenticated");

          const response: ConversationResponse = await getNewsFromUrl({
            user_id: userId,
            industry_type: "healthcare",
            query: "Give me the top 5 healthcare compliance news articles from each categories URLs with the most recent articles first.",
            categories: state.categories,
          });

          const promptsBySection = response.llmPromptsBySection ?? {};
          if (response.llmPrompt?.trim() && !promptsBySection.listing) {
            promptsBySection.listing = response.llmPrompt.trim();
          }
          setDiscoverLlmPromptsBySection(promptsBySection);
          setDiscoverLlmWallsBySection(
            response.llmInvokeWallSecondsBySection ?? {},
          );

          if (response && ((response.articles && response.articles.length > 0) || (response.news_items && response.news_items.length > 0))) {
            const transformedArticles: Article[] = [];

            if (response.articles && response.articles.length > 0) {
              transformedArticles.push(...response.articles.map((article) => ({
                ...article,
                source_url: (article as any).source_url?.trim() || "",
                categories: article.categories?.length > 0 ? article.categories : [state.category || "all-sources"],
              })));
            } else if (response.news_items && response.news_items.length > 0) {
              response.news_items.forEach((newsItem, index) => {
                transformedArticles.push({
                  id: `news-${index}-${Date.now()}`,
                  headline: newsItem.title,
                  bodyText: newsItem.description,
                  publishedTime: newsItem.date ? new Date(newsItem.date).toLocaleDateString() : new Date(response.timestamp).toLocaleDateString(),
                  image: response.images?.[index]?.url || response.images?.[0]?.url || "",
                  source_url: newsItem.url || "",
                  sources: [newsItem.url],
                  categories: [state.category || "all-sources"],
                  featured: index === 0,
                  likes: 0,
                  isLiked: false,
                });
              });
            }

            setArticles(transformedArticles);
          } else {
            setArticles([]);
            setNewsError("No articles found.");
          }
        } catch (error: any) {
          console.error("Error fetching news & announcements:", error);
          setNewsError(error.message || "Failed to fetch news");
          setArticles([]);
        } finally {
          setLoadingNews(false);
        }
      };

      fetchAllUpdates();
      return;
    }

    // My Sources with no saved interests - show empty state
    if (state?.category === "my-sources" && (!state?.categories || state.categories.length === 0)) {
      setSelectedCategory("my-sources");
      setSelectedLinkName("My Sources");
      setArticles([]);
      setNewsError("Save interests to see articles from My Sources.");
      setLoadingNews(false);
      return;
    }

    // If URL is provided, fetch news from that URL
    if (state?.url) {
      setLoadingNews(true);
      setNewsError(null);
      setSelectedCategory(state.category || null);
      setSelectedLinkName(state.linkName || null);
      
      const fetchNewsFromUrl = async () => {
        try {
          const userId = parseInt(getCurrentUserId(), 10);
          if (!userId || userId === 0) {
            throw new Error("User not authenticated");
          }

          // Call the discover news API to fetch news from URL
          const response: ConversationResponse = await getNewsFromUrl({
            url: state.url!,
            user_id: userId,
            industry_type: "healthcare",
            query: "Give me the top 5 healthcare compliance news articles from the URL with the most recent articles first.",
            linkName: state.linkName || undefined,
          });

          const promptsSingle = response.llmPromptsBySection ?? {};
          if (response.llmPrompt?.trim() && !promptsSingle.listing) {
            promptsSingle.listing = response.llmPrompt.trim();
          }
          setDiscoverLlmPromptsBySection(promptsSingle);
          setDiscoverLlmWallsBySection(
            response.llmInvokeWallSecondsBySection ?? {},
          );

          // Transform ConversationResponse into Article format
          // Priority: articles > news_items > sources
          if (response && ((response.articles && response.articles.length > 0) || (response.news_items && response.news_items.length > 0) || (response.sources && response.sources.length > 0))) {
            const transformedArticles: Article[] = [];
            
            // Priority 1: Use articles array if available (newest format)
            // Each article must have source_url (URL it was scraped from) for conversion API
            if (response.articles && response.articles.length > 0) {
              const requestUrls = state.url?.includes(",")
                ? state.url.split(",").map((u: string) => u.trim()).filter(Boolean)
                : state.url
                  ? [state.url]
                  : response.urls ?? [];
              transformedArticles.push(...response.articles.map((article, index) => ({
                ...article,
                // Preserve or fallback source_url so conversion API always has the correct URL
                source_url: (article as any).source_url?.trim() || requestUrls[index] || requestUrls[0] || "",
                categories: state.category && !article.categories.includes(state.category)
                  ? [...article.categories, state.category]
                  : article.categories,
              })));
            } 
            // Priority 2: Use news_items if available
            else if (response.news_items && response.news_items.length > 0) {
              const requestUrls = state.url?.includes(",")
                ? state.url.split(",").map((u: string) => u.trim()).filter(Boolean)
                : state.url
                  ? [state.url]
                  : response.urls ?? [];
              response.news_items.forEach((newsItem, index) => {
                transformedArticles.push({
                  id: `news-${index}-${Date.now()}`,
                  headline: newsItem.title,
                  bodyText: newsItem.description,
                  publishedTime: newsItem.date ? new Date(newsItem.date).toLocaleDateString() : new Date(response.timestamp).toLocaleDateString(),
                  image: response.images && response.images.length > index 
                    ? response.images[index].url 
                    : (response.images && response.images.length > 0 
                      ? response.images[0].url 
                      : ""),
                  source_url: requestUrls[index] || requestUrls[0] || newsItem.url || "",
                  sources: [newsItem.url],
                  categories: state.category ? [state.category] : [],
                  featured: index === 0,
                  likes: 0,
                  isLiked: false,
                });
              });
            } 
            // Priority 3: Fall back to sources array
            else if (response.sources && response.sources.length > 0) {
              const singleRequestUrl = state.url?.includes(",") ? null : (state.url || response.url || "");
              response.sources.forEach((source, index) => {
                transformedArticles.push({
                  id: `source-${index}-${Date.now()}`,
                  headline: source.title,
                  bodyText: source.description,
                  publishedTime: new Date(response.timestamp).toLocaleDateString(),
                  image: response.images && response.images.length > index 
                    ? response.images[index].url 
                    : (response.images && response.images.length > 0 
                      ? response.images[0].url 
                      : ""),
                  source_url: singleRequestUrl || source.url || "",
                  sources: [source.url],
                  categories: state.category ? [state.category] : [],
                  featured: index === 0,
                  likes: 0,
                  isLiked: false,
                });
              });
            }

            if (transformedArticles.length > 0) {
              setArticles(transformedArticles);
            } else {
              // No articles from API — show empty state
              setArticles([]);
              setNewsError("No articles found from this source.");
            }
          } else {
            // No response — show empty state
            setArticles([]);
            setNewsError("No articles found from this source.");
          }
        } catch (error: any) {
          console.error("Error fetching news from URL:", error);
          setNewsError(error.message || "Failed to fetch news from URL");
          // Do not show default articles on error
          setArticles([]);
        } finally {
          setLoadingNews(false);
        }
      };

      fetchNewsFromUrl();
    } else if (state?.category) {
      setSelectedCategory(state.category);
      setSelectedLinkName(null);
      setArticles([]);

      // Update interests to highlight the selected category
      setInterests((interests) =>
        interests.map((interest) =>
          interest.id === state.category
            ? { ...interest, selected: true }
            : interest
        )
      );
    } else {
      setSelectedCategory(null);
      setSelectedLinkName(null);
      setArticles([]);
    }
  }, [location.state]);

  // Log category selection to audit log
  useEffect(() => {
    if (!selectedCategory) return;
    
    const logCategoryAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      const facility = getSelectedFacilityFromSession();
      const categoryLabel = getCategoryLabelById(selectedCategory);
      
      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: '/dashboard/discover',
        method: 'GET',
        request_payload: {
          category: selectedCategory,
        },
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: 'Discover',
          tab: categoryLabel,
          action: 'VIEW',
        },
        action_type: 'VIEW',
        action_status: 'SUCCESS',
      });
    };
    
    logCategoryAccess();
  }, [selectedCategory]);

  const handleSourceTabClick = (tabId: "all-sources" | "my-sources") => {
    const activeLinks = discoverLinks.filter((l) => l.is_active);
    const categories =
      tabId === "all-sources"
        ? activeLinks.slice(0, 3).map((l) => ({ name: l.pretty_name, urls: [l.url] }))
        : activeLinks
            .filter((l) => {
              const id = l.pretty_name.toLowerCase().replace(/\s+/g, "-");
              return interests.some((i) => i.id === id && i.selected);
            })
            .map((l) => ({ name: l.pretty_name, urls: [l.url] }));

    navigate(location.pathname, {
      replace: true,
      state: {
        category: tabId,
        linkName: tabId === "all-sources" ? "All Sources" : "My Sources",
        categories,
        cachedInterests: interests,
      },
    });
  };

  const handleLikeClick = (articleId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation
    setArticles((articles) =>
      articles.map((article) =>
        article.id === articleId
          ? {
              ...article,
              isLiked: !article.isLiked,
              likes: article.isLiked ? article.likes - 1 : article.likes + 1,
            }
          : article
      )
    );
  };

  const handleInterestToggle = (topicId: string) => {
    setInterests((interests) =>
      interests.map((interest) =>
        interest.id === topicId
          ? { ...interest, selected: !interest.selected }
          : interest
      )
    );
  };

  const handleSaveInterests = async () => {
    const userId = parseInt(getCurrentUserId(), 10);
    if (!userId || userId === 0) return;
    const selectedIds = interests.filter((i) => i.selected).map((i) => i.id);
    setSavingInterests(true);
    try {
      await saveUserInterests(userId, selectedIds);
      window.dispatchEvent(new CustomEvent("interestsSaved", { detail: { selectedIds } }));
      toast.success("Interests saved successfully");

      // After saving, only auto-refresh "My Sources" when the user is
      // currently on the My Sources tab. If they are on All Sources or
      // any other Discover view, keep the current page as-is.
      // When all categories are removed, this will refresh to the
      // "no interests" empty-state message for My Sources.
      if (selectedCategory === "my-sources") {
        handleSourceTabClick("my-sources");
      }
    } catch (err) {
      console.error("Failed to save interests:", err);
      toast.error("Failed to save interests. Please try again.");
    } finally {
      setSavingInterests(false);
    }
  };

  const handleArticleClick = (articleId: string) => {
    // Find the article in current articles state (includes API articles)
    const articleData = articles.find((a) => a.id === articleId);
    // Pass url and linkName so ArticleDetail can pass them back on back navigation
    const state = location.state as any;
    navigate(`/dashboard/discover/article/${articleId}`, {
      state: {
        category: selectedCategory,
        article: articleData || null,
        url: state?.url || null,
        linkName: state?.linkName || selectedLinkName || null,
        categories: state?.categories || null,
        cachedArticles: articles,
        cachedInterests: interests,
      },
    });
  };

  const getConversationUrlFromArticle = (article: Article): string | null => {
    if (article.source_url && article.source_url.trim()) {
      const trimmed = article.source_url.trim();
      if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        return trimmed;
      }
    }

    const firstSource = article.sources && article.sources[0];
    if (!firstSource) return null;

    if (typeof firstSource === "object" && firstSource.url) {
      return firstSource.url;
    }

    const src = typeof firstSource === "string" ? firstSource : "";
    if (src.startsWith("http://") || src.startsWith("https://")) {
      return src;
    }

    return resolveSourceUrl(src);
  };

  const handleAskFromCard = async (article: Article, question: string) => {
    const q = question.trim();
    if (!q) return;

    const sourceUrl = getConversationUrlFromArticle(article);
    if (!sourceUrl) {
      setActiveQa({
        article,
        question: q,
        answer: "",
        followUps: [],
        sources: [],
        loading: false,
        error: "No source URL available for this article.",
      });
      return;
    }

    const userId = getCurrentUserId();
    if (!userId || userId === "0") {
      setActiveQa({
        article,
        question: q,
        answer: "",
        followUps: [],
        sources: [],
        loading: false,
        error: "User not authenticated. Please log in again.",
      });
      return;
    }

    setActiveQa({
      article,
      question: q,
      answer: "",
      followUps: [],
      sources: [],
      loading: true,
      error: null,
    });

    try {
      const response = await sendConversation({
        user_id: parseInt(userId, 10),
        industry_type: "healthcare",
        query: q,
        url: sourceUrl,
        conversation_id: conversationId || null,
      });

      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }

      setActiveQa((prev) =>
        prev
          ? {
              ...prev,
              loading: false,
              answer: response.summary,
              followUps: response.suggested_follow_ups || [],
              sources: response.sources || [],
            }
          : prev
      );
    } catch (error: any) {
      setActiveQa((prev) =>
        prev
          ? {
              ...prev,
              loading: false,
              error: error?.message || "Failed to get a response. Please try again.",
            }
          : prev
      );
    }
  };

  const handleFollowUpClick = (followUp: string) => {
    if (!activeQa?.article) return;
    handleAskFromCard(activeQa.article, followUp);
  };

  const renderSourceLogos = (sources: (string | { name: string; url: string })[]) => {
    const localSources = discoveryData.sources;
    return (
      <SourceLogos>
        {sources.map((source, index) => {
          // Handle object source {name, url}
          if (typeof source === 'object' && source !== null) {
            const initials = source.name.substring(0, 2).toUpperCase();
            const key = source.url || source.name;
            return (
              <SourceLogo key={key} style={{ zIndex: sources.length - index }}>
                {initials}
              </SourceLogo>
            );
          }
          // Handle string source
          const isUrl = source.startsWith("http://") || source.startsWith("https://");
          if (isUrl) {
            try {
              const initials = new URL(source).hostname.replace("www.", "").substring(0, 2).toUpperCase();
              return <SourceLogo key={source} style={{ zIndex: sources.length - index }}>{initials}</SourceLogo>;
            } catch {
              return <SourceLogo key={source} style={{ zIndex: sources.length - index }}>??</SourceLogo>;
            }
          }
          // Short ID — check local data
          const local = localSources.find((s) => s.id === source);
          const initials = local ? local.name.substring(0, 2).toUpperCase() : source.substring(0, 2).toUpperCase();
          return <SourceLogo key={source} style={{ zIndex: sources.length - index }}>{initials}</SourceLogo>;
        })}
      </SourceLogos>
    );
  };

  const resolveSourceUrl = (sourceId: string): string | null => {
    const map: Record<string, string> = {
      "hhs-gov": "https://www.hhs.gov",
      "hipaa-journal": "https://www.hipaajournal.com",
      "rural-health-info-hub": "https://www.ruralhealthinfo.org",
      nrha: "https://www.ruralhealth.us",
      "abc-news": "https://abcnews.go.com",
      cbs19: "https://www.cbs19.tv",
      independent: "https://www.independent.co.uk",
      dea: "https://www.dea.gov",
      epa: "https://www.epa.gov",
      sox: "https://www.sec.gov/spotlight/sarbanes-oxley.htm",
    };
    return map[sourceId] || null;
  };

  const getPrimaryCategoryForArticle = (article: Article): string | null => {
    const allIds = article.categories || [];
    // Prefer currently selected category if this article belongs to it
    const idToUse =
      (selectedCategory && allIds.includes(selectedCategory) && selectedCategory) ||
      allIds[0] ||
      null;
    if (!idToUse) return null;
    return getCategoryLabelById(idToUse);
  };

  const featuredArticles = articles.map((article) => ({
    ...article,
    categoryLabel: getPrimaryCategoryForArticle(article),
  })).filter((article) => article.featured);

  const regularArticles = articles.map((article) => ({
    ...article,
    categoryLabel: getPrimaryCategoryForArticle(article),
  })).filter((article) => !article.featured);

  // Check if Regulatory Updates category is selected
  const isLatestUpdatesCategory = selectedCategory === "latest-updates";

  return (
    <DiscoveryContainer>
      {/* Header */}
      <Header>
        <HeaderLeft>
          <BallotIcon size={28} color="#666" />
          <HeaderTitle>
            Discover{" "}
            {selectedLinkName
              ? ` - ${selectedLinkName}`
              : selectedCategory
              ? ` - ${getCategoryLabelById(selectedCategory)}`
              : ""}
          </HeaderTitle>
          {(
            [
              ["listing", "Discover — listing LLM prompt"],
              ["fullcontent", "Discover — fullContent LLM prompt"],
            ] as const
          ).map(([key, title]) => (
            <GlobalAdminLlmPromptButton
              key={key}
              dialogTitle={title}
              promptText={discoverLlmPromptsBySection[key]}
              wallTimeSec={discoverLlmWallsBySection[key]}
            />
          ))}
        </HeaderLeft>

        {/* All Sources / My Sources tabs - top right */}
        {!isLatestUpdatesCategory && (() => {
          const hasActive = selectedCategory === "all-sources" || selectedCategory === "my-sources";
          return (
            <SourceTabsWrapper hasActive={hasActive}>
              <SourceTab
                $active={selectedCategory === "all-sources"}
                onClick={() => handleSourceTabClick("all-sources")}
              >
                All Sources
              </SourceTab>
              {!hasActive && (
                <Box sx={{ width: "1px", backgroundColor: "#99d4e1", my: "6px", flexShrink: 0 }} />
              )}
              <SourceTab
                $active={selectedCategory === "my-sources"}
                onClick={() => handleSourceTabClick("my-sources")}
              >
                My Sources
              </SourceTab>
            </SourceTabsWrapper>
          );
        })()}
      </Header>

      {/* Loading indicator */}
      {loadingNews && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <CircularProgress />
          <Typography sx={{ color: "#6b7280" }}>
            Fetching news...
          </Typography>
        </Box>
      )}

      {/* Error message */}

      {/* Regulatory Updates: special layout */}
      {!loadingNews && isLatestUpdatesCategory ? (
        <LatestUpdates
          initialSomTag={(location.state as { somTag?: string } | null)?.somTag}
          initialSectionId={(location.state as { sectionId?: number } | null)?.sectionId}
        />
      ) : !loadingNews ? (
        <>
          {/* Action Buttons */}
          {/* <ActionButtons>
        <RoundActionButton>
          <BookmarkIcon />
        </RoundActionButton>
        <RoundActionButton>
          <ShareIcon />
        </RoundActionButton>
        <RoundActionButton>
          <HelpIcon />
        </RoundActionButton>
      </ActionButtons> */}

          {/* Content */}
          <ContentArea>
            <MainContent>
              {/* Error message in main content area */}
              {newsError && (
                <Box
                  sx={{
                    padding: "24px 28px",
                    marginBottom: "16px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "10px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#dc2626",
                      fontSize: "16px",
                      fontWeight: 500,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      lineHeight: 1.8,
                    }}
                  >
                    {newsError}
                  </Typography>
                </Box>
              )}

              {/* Featured Article */}
              {featuredArticles.map((article) => (
                <HorizontalArticleCard
                  key={article.id}
                  article={article}
                  onClick={() => handleArticleClick(article.id)}
                  onSourcesClick={(e, sources) => {
                    e.stopPropagation();
                    setModalSourceIds(sources);
                    setIsSourcesOpen(true);
                  }}
                  onLikeClick={handleLikeClick}
                  renderSourceLogos={renderSourceLogos}
                  categoryLabel={(article as any).categoryLabel ?? null}
                  onAskQuestion={(q) => handleAskFromCard(article, q)}
                />
              ))}

              {/* Regular Articles Grid */}
              <Box sx={{ display: "flex", gap: "16px" }}>
                {regularArticles.slice(0, 3).map((article) => (
                  <RegularArticleCard
                    key={article.id}
                    article={article}
                    onClick={() => handleArticleClick(article.id)}
                    onSourcesClick={(e, sources) => {
                      e.stopPropagation();
                      setModalSourceIds(sources);
                      setIsSourcesOpen(true);
                    }}
                    onLikeClick={handleLikeClick}
                    renderSourceLogos={renderSourceLogos}
                    onAskQuestion={(q) => handleAskFromCard(article, q)}
                  />
                ))}
              </Box>

              {/* More Articles */}
              {regularArticles.slice(3).map((article) => (
                <HorizontalArticleCard
                  key={article.id}
                  article={article}
                  onClick={() => handleArticleClick(article.id)}
                  onSourcesClick={(e, sources) => {
                    e.stopPropagation();
                    setModalSourceIds(sources);
                    setIsSourcesOpen(true);
                  }}
                  onLikeClick={handleLikeClick}
                  renderSourceLogos={renderSourceLogos}
                  categoryLabel={(article as any).categoryLabel ?? null}
                    onAskQuestion={(q) => handleAskFromCard(article, q)}
                />
              ))}
            </MainContent>

            {/* Sidebar */}
            <Sidebar>
              {activeQa && (
                <Box
                  sx={{
                    mb: 2,
                    backgroundColor: "#ffffff",
                    border: "1px solid #b9e3ed",
                    borderRadius: "10px",
                    padding: "16px",
                    boxShadow: "0px 4px 12px rgba(5, 89, 108, 0.08)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#0a829d",
                      }}
                    >
                      AI
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setActiveQa(null)}
                      sx={{
                        ml: 1,
                        width: 24,
                        height: 24,
                        borderRadius: "999px",
                        backgroundColor: "#f1fafc",
                        color: "#6b7280",
                        "&:hover": { backgroundColor: "#e0f5fb" },
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </IconButton>
                  </Box>
                  {activeQa.article && (
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#111827",
                        mb: 1,
                      }}
                    >
                      {activeQa.article.headline}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: "999px",
                        border: "1px solid #0a829d",
                        backgroundColor: "#0a829d",
                        color: "#ffffff",
                        fontSize: "12px",
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      {activeQa.question}
                    </Box>
                    {activeQa.followUps.map((fu, idx) => (
                      <Box
                        key={idx}
                        onClick={() => handleFollowUpClick(fu)}
                        sx={{
                          borderRadius: "999px",
                          border: "1px solid #b9e3ed",
                          backgroundColor: "#ffffff",
                          color: "#0a829d",
                          fontSize: "12px",
                          px: 1.5,
                          py: 0.5,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "#e6f9fc",
                            borderColor: "#0a829d",
                          },
                        }}
                      >
                        {fu}
                      </Box>
                    ))}
                  </Box>

                  {activeQa.loading && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      <CircularProgress size={16} />
                      <span>Thinking about this question…</span>
                    </Box>
                  )}

                  {!activeQa.loading && activeQa.error && (
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#b91c1c",
                      }}
                    >
                      {activeQa.error}
                    </Typography>
                  )}

                  {!activeQa.loading && !activeQa.error && activeQa.answer && (
                    <>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#1f2937",
                          lineHeight: 1.6,
                          whiteSpace: "pre-line",
                          mt: 0.5,
                        }}
                      >
                        {activeQa.answer}
                      </Typography>

                      {activeQa.sources && activeQa.sources.length > 0 && (
                        <Box
                          sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: "1px solid #e5f3f7",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#0a829d",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              mb: 0.5,
                            }}
                          >
                            Sources
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            {activeQa.sources.map((src: any, idx: number) => (
                              <Box key={idx}>
                                {src.title && (
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      color: "#111827",
                                    }}
                                  >
                                    {src.title}
                                  </Typography>
                                )}
                                {src.url && (
                                  <CmsSourceLink
                                    url={src.url}
                                    style={{
                                      fontSize: "11px",
                                      color: "#0a829d",
                                      textDecoration: "none",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {src.url}
                                  </CmsSourceLink>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {!activeQa && (
                <InterestsCard>
                  <InterestsTitle>Make it yours</InterestsTitle>
                  <InterestsDescription>
                    Select topics and interests to customize your Discover experience
                  </InterestsDescription>

                  <InterestsTags>
                    {interestsLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '4px' }}>
                        <CircularProgress size={16} />
                        <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>Loading topics…</Typography>
                      </Box>
                    ) : interests.length === 0 ? (
                      <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>No topics available.</Typography>
                    ) : (
                      interests.map((interest) => (
                        <InterestTag
                          key={interest.id}
                          selected={interest.selected}
                          icon={<MenuBookIcon size={16} />}
                          label={interest.label}
                          onClick={() => handleInterestToggle(interest.id)}
                          clickable
                        />
                      ))
                    )}
                  </InterestsTags>

                  <SaveInterestsButton onClick={handleSaveInterests} disabled={savingInterests}>
                    {savingInterests ? <CircularProgress size={16} sx={{ color: "#e6fafc" }} /> : <StarIcon size={16} filled color="#e6fafc" />}
                    &nbsp; {savingInterests ? "Saving..." : "Save Interests"}
                  </SaveInterestsButton>
                </InterestsCard>
              )}
            </Sidebar>
          </ContentArea>

          {/* Sources Modal */}
          <Dialog
            open={isSourcesOpen}
            onClose={(event, reason) => {
              // Prevent closing on backdrop click or escape key
              if (reason === "backdropClick" || reason === "escapeKeyDown") {
                return;
              }
              setIsSourcesOpen(false);
            }}
            fullWidth
            maxWidth="sm"
            BackdropProps={{
              sx: {
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              },
            }}
          >
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Sources</span>
                <IconButton
                  onClick={() => setIsSourcesOpen(false)}
                  aria-label="Close sources"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {modalSourceIds.map((sourceEntry, idx) => {
                  let name: string;
                  let url: string | null;
                  let key: string;
                  if (typeof sourceEntry === 'object' && sourceEntry !== null) {
                    name = (sourceEntry as ArticleSource).name;
                    url = (sourceEntry as ArticleSource).url || null;
                    key = url || name + idx;
                  } else {
                    const isUrl = sourceEntry.startsWith('http://') || sourceEntry.startsWith('https://');
                    if (isUrl) {
                      try { name = new URL(sourceEntry).hostname.replace('www.', ''); } catch { name = sourceEntry; }
                      url = sourceEntry;
                    } else {
                      const local = discoveryData.sources.find((s) => s.id === sourceEntry);
                      name = local?.name || sourceEntry;
                      url = resolveSourceUrl(sourceEntry);
                    }
                    key = sourceEntry;
                  }
                  return (
                    <Box
                      key={key}
                      sx={{ display: "flex", flexDirection: "column" }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                        {name}
                      </Typography>
                      {url ? (
                        <CmsSourceLink
                          url={url}
                          style={{
                            color: "#0a829D",
                            fontSize: "12px",
                            wordBreak: "break-all",
                          }}
                        >
                          {url}
                        </CmsSourceLink>
                      ) : (
                        <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                          Link not available
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </DiscoveryContainer>
  );
}
